import Axios, { CancelToken } from 'axios';

import { SubmissionError } from '../form';
import keycloak from '../keycloak';

const cancelTokens = {};

function apiRequest({ actionName, actionParams, payload, requestSource, requestParams }) {
    return { actionParams, payload, requestParams, requestSource, type: `${actionName}_REQUEST` };
}

function apiSuccess({ actionName, actionParams, payload, requestSource, requestParams }) {
    return { actionParams, payload, requestParams, requestSource, type: `${actionName}_SUCCESS` };
}

function apiFailure({ actionName, actionParams, payload, requestParams }) {
    return { actionParams, payload, requestParams, type: `${actionName}_FAILURE` };
}

function requestFailure({ actionName, payload, requestParams }) {
    return { payload, requestParams, type: `${actionName}_REQUEST_FAILURE` };
}

let previousPathname;

export function cancelAllWithRouteChange({ pathname }) {
    if (previousPathname !== pathname) {
        previousPathname = pathname;
        Object.values(cancelTokens)
            .filter(({ cancelOnRouteChange }) => cancelOnRouteChange)
            .forEach(({ cancel }) => cancel('RouteChange'));
    }
}

export default function apiRequestActionCreator(props) {
    const {
        actionName,
        actionParams,
        asyncDispatch,
        requestSource,
        cancelOnRouteChange,
        ignoreFailure = false,
        onDownloadProgress = () => {},
        params,
        processError = error => error,
        processSuccessResponse = ({ data }) => data,
        timeoutSeconds,
        blockParallel = !!timeoutSeconds,
        snackbarErrorField,
    } = props;
    return async (dispatch, getState, { axios }) => {
        if (blockParallel && cancelTokens[actionName]) {
            cancelTokens[actionName].cancel('BlockParallel');
        }

        const currentCancelToken = { ...CancelToken.source(), cancelOnRouteChange };
        cancelTokens[actionName] = currentCancelToken;

        const serializableRequestParams = {
            method: 'get',
            withCredentials: true,
            ...params,

            headers: {
                Authorization: `bearer ${keycloak.token}`,
                'Content-Type': 'application/json',
                ...params.headers,
            },
        };

        const requestParams = {
            ...serializableRequestParams,
            cancelToken: currentCancelToken.token,
            onDownloadProgress: (...rest) => onDownloadProgress(dispatch, ...rest),
        };

        const dispatchRequestAction = () =>
            dispatch(
                apiRequest({
                    actionName,
                    actionParams,
                    payload: props,
                    requestParams: serializableRequestParams,
                    requestSource,
                })
            );
        if (asyncDispatch) {
            setImmediate(dispatchRequestAction);
        } else {
            dispatchRequestAction();
        }

        let timeoutTimer;

        try {
            if (timeoutSeconds) {
                timeoutTimer = setTimeout(() => {
                    currentCancelToken.cancel('Timeout');
                }, timeoutSeconds * 1000);
            }

            const response = await axios.request(requestParams);
            const payload = processSuccessResponse(response, params, dispatch);

            const dispatchSuccessAction = () =>
                dispatch(
                    apiSuccess({ actionName, actionParams, payload, requestParams, requestSource })
                );
            if (asyncDispatch) {
                setImmediate(dispatchSuccessAction);
            } else {
                dispatchSuccessAction();
            }

            return payload;
        } catch (error) {
            if (Axios.isCancel(error)) {
                console.debug(`${actionName} was cancelled: ${error.message}`);
            }

            if (error.response && error.response.status === 401) {
                // keycloak.logout();
            }

            const dispatchFailure = actionData => {
                if (!Axios.isCancel(error) || error.message === 'Timeout') {
                    if (ignoreFailure) {
                        return;
                    }
                    const dispatchFailureAction = () => dispatch(actionData);
                    if (asyncDispatch) {
                        setImmediate(dispatchFailureAction);
                    } else {
                        dispatchFailureAction();
                    }
                }
            };

            dispatchFailure(requestFailure({ actionName, payload: error, requestParams }));
            let payload;

            try {
                payload = processError(error, dispatch);
            } catch (e) {
                if (e instanceof SubmissionError) {
                    if (e.fields[snackbarErrorField]) {
                        payload = new Error(e.fields[snackbarErrorField]);
                        dispatchFailure(
                            apiFailure({ actionName, actionParams, payload, requestParams })
                        );
                    }
                }

                throw e;
            }

            dispatchFailure(apiFailure({ actionName, actionParams, payload, requestParams }));

            // We could re-throw here, but this causes lots of errors in console
            return { ...payload, isError: true };
        } finally {
            clearTimeout(timeoutTimer);
        }
    };
}
